/**
 * Lazily loads the Google Maps JS API (Places library) for the Emergency
 * Request form's typed-address autocomplete — see
 * EMERGENCY_BOOKING_MODULE_CHECKLIST.md for why this is gated behind an env
 * var rather than always-on: no Google Maps API key/billing is set up for
 * this project yet.
 *
 * Set REACT_APP_GOOGLE_MAPS_API_KEY (see .env.example) to enable it. Until
 * then, isGoogleMapsConfigured() returns false and no script is ever
 * injected — callers must check it before calling loadGoogleMapsPlaces().
 *
 * "Use current location" does NOT depend on this — it already works via the
 * existing free OpenStreetMap/Nominatim reverseGeocodeAddress() utility
 * (src/utils/reverseGeocode.js), with no API key required.
 */

let loadPromise = null;

export function isGoogleMapsConfigured() {
  return Boolean(process.env.REACT_APP_GOOGLE_MAPS_API_KEY);
}

export function loadGoogleMapsPlaces() {
  if (!isGoogleMapsConfigured()) {
    return Promise.resolve(null);
  }
  if (window.google?.maps?.places) {
    return Promise.resolve(window.google);
  }
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById("nema-google-maps-script");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.google));
      existing.addEventListener("error", () => reject(new Error("Failed to load Google Maps")));
      return;
    }
    const script = document.createElement("script");
    script.id = "nema-google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      process.env.REACT_APP_GOOGLE_MAPS_API_KEY
    )}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = () => reject(new Error("Failed to load Google Maps"));
    document.head.appendChild(script);
  }).catch((err) => {
    loadPromise = null;
    throw err;
  });

  return loadPromise;
}
